import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { signupOwner } from './api/signup';
import SimplePopup from "./SimplePopup";

// Styled Components
const Container = styled.div`
  position: relative; 
  width: 100%;
  max-width: 550px;
  margin: 0 auto;
  padding: 30px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 0 20px rgba(0,0,0,0.2);
  max-height: 90vh;        // Limit height to viewport
  overflow-y: auto;        // Enable vertical scroll if content exceeds
`;


const Title = styled.h2`
  text-align: center;
  color: #b30000;
  margin-bottom: 30px;
  font-size: 1.8rem;
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #ccc;
  outline: none;
  font-size: 1rem;

  &:focus {
    border-color: #b30000;
    box-shadow: 0 0 5px rgba(179, 0, 0, 0.3);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  background: #b30000;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 20px;

  &:hover {
    background: #990000;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 1.6rem;
  font-weight: bold;
  color: #b30000;
  cursor: pointer;
  z-index: 20;
`;

const countryOptions = [
  { name: "India", code: "+91", flag: "🇮🇳" },
  { name: "United States", code: "+1", flag: "🇺🇸" },
  { name: "United Kingdom", code: "+44", flag: "🇬🇧" },
  { name: "Canada", code: "+1", flag: "🇨🇦" },
  { name: "Germany", code: "+49", flag: "🇩🇪" },
  { name: "Australia", code: "+61", flag: "🇦🇺" },
  { name: "France", code: "+33", flag: "🇫🇷" },
  { name: "Japan", code: "+81", flag: "🇯🇵" },
  { name: "China", code: "+86", flag: "🇨🇳" },
  { name: "Brazil", code: "+55", flag: "🇧🇷" },
];

const SignupForm = ({ onClose, selectedPlan, selectedPlanSpan }) => {
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    confirm: '',
    countryCode: '+91',
    phone: '',
    storeName: '',
    storeAddress: '',
  });

  const [popup, setPopup] = useState({ open: false, message: "", isSuccess: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    const phoneRegex = /^\d+$/;
    if (!phoneRegex.test(form.phone)) {
      setError("Phone number must contain digits only.");
      return;
    }
    if (form.phone.length < 7 || form.phone.length > 15) {
      setError("Phone number length must be between 7 and 15 digits.");
      return;
    }

    // Build payload for Django backend
    const payload = {
      username: form.username,
      password: form.password,
      email: form.email,
      phone: form.phone,
      store_name: form.storeName,
      store_address: form.storeAddress,
      plan_name: selectedPlan,
      plan_span: selectedPlanSpan,
    };

    setLoading(true);

    try {
      const res = await signupOwner(payload);
      setLoading(false);
      setPopup({
        open: true,
        message: (
          <>
            Registered successfully! Check your mail for verification.
          </>
        ),
        isSuccess: true
      });
    } catch (error) {
      setLoading(false);
      let errorMessage = "Registration failed. Please check your details and try again.";
      
      if (error.response?.data) {
        const data = error.response.data;
        
        // Handle specific field errors
        if (data.username) {
          errorMessage = "Username already exists. Please choose a different username.";
        } else if (data.email) {
          errorMessage = "Email already exists. Please use a different email address.";
        } else if (data.store_name) {
          errorMessage = "Store name already exists. Please choose a different store name.";
        } else if (data.phone) {
          errorMessage = "Phone number already exists. Please use a different phone number.";
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        } else {
          // Handle other validation errors
          const firstError = Object.values(data)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else if (firstError) {
            errorMessage = firstError;
          }
        }
      } else if (error.message === 'Network Error') {
        errorMessage = "Network error. Please check your internet connection and try again.";
      }
      
      setPopup({
        open: true,
        message: errorMessage,
        isSuccess: false
      });
    }
  }

  return (
    <Container>
      <CloseButton onClick={onClose}>×</CloseButton>
      <form onSubmit={handleSubmit}>
        <Title>Sign Up</Title>
        <FormGroup>
          <Label>Username</Label>
          <Input
            name="username"
            value={form.username}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Email ID</Label>
          <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Password</Label>
          <Input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Confirm Password</Label>
          <Input
            type="password"
            name="confirm"
            value={form.confirm}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Phone Number</Label>
          <Row>
            <select
              style={{
                width: '35%',
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid #ccc',
                fontSize: '1rem',
              }}
              name="countryCode"
              value={form.countryCode}
              onChange={handleChange}
              required
            >
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name} ({country.code})
                </option>
              ))}
            </select>
            <Input
              style={{ width: '70%' }}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
            />
          </Row>
        </FormGroup>

        <FormGroup>
          <Label>Store Name</Label>
          <Input
            type="text"
            name="storeName"
            value={form.storeName}
            onChange={handleChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label>Store Address</Label>
          <Input
            type="text"
            name="storeAddress"
            value={form.storeAddress}
            onChange={handleChange}
            required
          />
        </FormGroup>

        <Button type="submit">Register</Button>

      </form>
      {popup.open && (
        <SimplePopup
          message={popup.message}
          onClose={() => {
            const wasSuccess = popup.isSuccess;
            setPopup({ open: false, message: "", isSuccess: false });
            // Only close the form if registration was successful
            if (wasSuccess) {
              onClose();
            }
            // If it was an error, keep the form open so user can fix and retry
          }}
        />
      )}
    </Container>
  );
};

export default SignupForm;
